import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  alpha,
  Skeleton,
  Tab,
  Table as MUITable,
  TableContainer,
  Tabs,
} from '@mui/material';
import classNames from 'classnames';
import React, {
  ChangeEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { useLocation } from 'react-router';
import { useAppSelector } from 'src/app/hooks';
import { paginationDefaultState } from 'src/constants/paginationConstants';
import { Role } from 'src/store/models/Auth';
import { TicketStatus } from 'src/store/models/Ticket';
import { colors } from 'src/utils/colors';
import { onKeyDown } from 'src/utils/keyboardAccessibility';
import { useQueryParams } from 'src/utils/useQueryParams';

import { routes } from '../../../routes';
import LabelCheckbox from '../LabelCheckbox/LabelCheckbox';
import {
  TableFilter,
  TableHeader,
  TableQueryParameters,
  TableSortParameters,
} from './Table.interface';
import classes from './Table.module.scss';
import TableSearch from './TableSearch/TableSearch';
import { determineNextOrder, getSortIcon } from './utils/TableHelperFunctions';

interface TableProps<S, T> {
  headers: TableHeader<T>[];
  data: T[];
  emptyTableComponent?: ReactNode;
  className?: string;
  onRowClick?: (rowData: T) => void;
  disabled?: boolean;
  loading?: boolean;
  filterItems?: TableFilter<S>[];
  onFilterChange?: (item: TableFilter<S>) => void;
  onSearchChange?: (search: string) => void;
  onSortClick?: (sortItem: keyof T | string) => void;
  sortParams?: TableSortParameters<T>;
  search?: boolean;
  searchPlaceholder?: string;
  disableHover?: boolean;
  endNode?: (row: T) => ReactNode;
  highlightedId?: string;
  highlightColor?: string;
  selectable?: boolean;
  selectedRows?: T[];
  handleCheckboxChange?: (checked: boolean, data: T) => void;
  isSelected?: (data: T) => boolean;
  classes?: {
    container?: string;
    header?: string;
  };
}

const Table = <S, T>({
  headers,
  data,
  emptyTableComponent,
  className,
  onRowClick,
  disabled = false,
  loading = false,
  filterItems,
  onFilterChange,
  onSearchChange,
  onSortClick,
  sortParams,
  search = false,
  searchPlaceholder,
  disableHover = false,
  endNode,
  highlightColor,
  highlightedId,
  selectable = false,
  selectedRows,
  handleCheckboxChange,
  isSelected,
  classes: tableClasses,
}: TableProps<S, T>): ReactElement => {
  const location = useLocation();

  const user = useAppSelector((state) => state.auth.user);
  const { urlParams, updateURLWithQueryParams } = useQueryParams<
    TableQueryParameters<S, T>
  >(location.search, {
    ...paginationDefaultState,
    s: filterItems?.[0]?.value,
  });

  const currentTab = filterItems?.find((item) => item.value === urlParams?.s);
  const rowRef = useRef<HTMLTableRowElement>(null);
  const status = urlParams?.s as unknown;

  useEffect(() => {
    if (!currentTab && filterItems?.length) {
      updateURLWithQueryParams(location.pathname, {
        ...urlParams,
        page: 0,
        s: filterItems?.[0]?.value,
      });
    }
    // eslint-disable-next-line
  }, [urlParams, filterItems]);

  const scrollToRow = () => {
    rowRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (highlightedId && rowRef?.current?.style) {
      setTimeout(() => {
        scrollToRow();
        if (rowRef.current?.style) {
          rowRef.current.style.backgroundColor = highlightColor;
        }
      }, 100);
      timeout = setTimeout(() => {
        rowRef.current.style.backgroundColor = null;
      }, 5000);
    }
    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line
  }, [highlightColor, highlightedId, rowRef.current, loading]);

  const handleSortClick = (sortItem: keyof T | string) => {
    if (onSortClick) {
      onSortClick(sortItem);
      return;
    }
    const orderByToBeSaved = determineNextOrder(urlParams, sortItem);
    const newParams: typeof urlParams = {
      ...urlParams,
      key: sortItem,
      mode: orderByToBeSaved,
    };

    updateURLWithQueryParams(location.pathname, newParams);
  };

  const checkAllSelected = () => {
    return (
      document.querySelectorAll(`td .${classes.Checkbox}`)?.length ===
        selectedRows?.length && !!selectedRows?.length
    );
  };

  const selectAll = (e: ChangeEvent<HTMLInputElement>) => {
    data?.forEach((row) => {
      if (!e?.target?.checked || !isSelected?.(row)) {
        handleCheckboxChange?.(e.target?.checked, row);
      }
    });
  };

  const tableFilter = (
    <Tabs
      className={classes.Tabs}
      value={urlParams?.s}
      onChange={(_, value) =>
        onFilterChange?.(filterItems.find((item) => item.value === value))
      }
      TabIndicatorProps={{
        style: {
          backgroundColor: colors()?.[currentTab?.color],
        },
      }}
    >
      {filterItems?.map((item, i) => (
        <Tab
          disabled={disabled || loading}
          label={item.label}
          value={item.value}
          key={i}
        />
      ))}
    </Tabs>
  );

  return (
    <>
      {filterItems?.length || search ? (
        <header
          data-table-header
          className={classNames(classes.Header, tableClasses?.header)}
        >
          {filterItems?.length ? tableFilter : null}
          {search && (
            <TableSearch
              urlParams={urlParams}
              disabled={disabled}
              placeholder={searchPlaceholder}
              onSearchChange={onSearchChange}
            />
          )}
        </header>
      ) : null}
      <TableContainer
        className={classNames(classes.Container, tableClasses?.container)}
      >
        <MUITable
          className={classNames(classes.Table, className, {
            [classes.Disabled]: disabled || loading,
          })}
          sx={
            !disabled && !loading
              ? {
                  '> tbody > tr:hover': {
                    cursor: onRowClick ? 'pointer' : 'unset',
                    backgroundColor:
                      !disableHover &&
                      alpha(
                        colors()?.[currentTab?.color] ??
                          colors()?.colorFumisGreen1,
                        0.1
                      ),
                  },
                }
              : null
          }
        >
          <thead>
            <tr>
              {selectable && !loading && !!data?.length && (
                <th
                  style={{
                    textAlign: 'left',
                  }}
                >
                  <LabelCheckbox
                    className={classes.Checkbox}
                    CheckboxProps={{
                      disableRipple: true,
                      checked: checkAllSelected(),
                      onChange: selectAll,
                    }}
                    color="primary"
                  />
                </th>
              )}
              {headers.map((header, index) => (
                <th
                  style={{
                    textAlign: header.align || 'left',
                    cursor: header.sortable ? 'pointer' : 'initial',
                  }}
                  key={index}
                  onClick={
                    !loading && header.sortable
                      ? () => handleSortClick(header.sortKey || header.accessor)
                      : null
                  }
                  tabIndex={0}
                  onKeyDown={
                    !loading && header.sortable
                      ? (event) =>
                          onKeyDown(event, () =>
                            handleSortClick(header.sortKey || header.accessor)
                          )
                      : null
                  }
                  aria-hidden={false}
                >
                  <span>{header.label}</span>
                  {header.sortable && (
                    <FontAwesomeIcon
                      icon={getSortIcon(
                        sortParams || urlParams,
                        header.sortKey || header.accessor
                      )}
                    />
                  )}
                </th>
              ))}
              {endNode && <th></th>}
            </tr>
          </thead>
          <tbody>
            {!loading &&
              data?.map((dataItem, dataIndex) => (
                <tr
                  onClick={!disabled ? () => onRowClick?.(dataItem) : null}
                  key={dataIndex}
                  ref={
                    highlightedId ===
                    dataItem?.['logCode' as keyof T]?.toString()
                      ? rowRef
                      : null
                  }
                >
                  {selectable && (
                    <td
                      style={{
                        textAlign: 'left',
                      }}
                    >
                      <LabelCheckbox
                        className={classes.Checkbox}
                        CheckboxProps={{
                          disableRipple: true,
                          checked: isSelected?.(dataItem),
                          onChange: (e) =>
                            handleCheckboxChange?.(e.target.checked, dataItem),
                        }}
                        color="primary"
                      />
                    </td>
                  )}
                  {headers?.map((header, headerIndex) => (
                    <td
                      style={{
                        textAlign: header.align || 'left',
                        fontWeight: header.fontWeight,
                      }}
                      key={headerIndex}
                    >
                      {header.component?.(dataItem, dataIndex) ??
                        (typeof dataItem?.[header.accessor] !== 'object'
                          ? ((dataItem?.[header?.accessor] ?? '/') as ReactNode)
                          : '/')}
                    </td>
                  ))}

                  {endNode && <td>{endNode(dataItem)}</td>}
                </tr>
              ))}
            {loading &&
              [...Array(urlParams?.size || 5)].map((_, i) => (
                <tr key={i} className={classes.Loading}>
                  <td
                    colSpan={
                      location.pathname === routes.TICKETS
                        ? headers.length - 1
                        : headers.length
                    }
                  >
                    <Skeleton animation="wave" variant="text" />
                  </td>
                  {location.pathname === routes.TICKETS && (
                    <td>
                      <Skeleton animation="wave" variant="text" />
                      {(status === TicketStatus.IN_PROGRESS ||
                        status === TicketStatus.RESOLVED) && (
                        <Skeleton animation="wave" variant="text" />
                      )}
                      {status === TicketStatus.RESOLVED && (
                        <Skeleton animation="wave" variant="text" />
                      )}
                      {status !== TicketStatus.UNASSIGNED &&
                        user?.role === Role.MANUFACTURER && (
                          <Skeleton animation="wave" variant="text" />
                        )}
                    </td>
                  )}

                  {endNode && <td></td>}
                </tr>
              ))}
          </tbody>
        </MUITable>
        {!data?.length && emptyTableComponent && !loading ? (
          <div className={classes.Empty}>{emptyTableComponent}</div>
        ) : null}
      </TableContainer>
    </>
  );
};

export default Table;
